import { useTranslation } from "react-i18next";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import ActivityLog from "./ActivityLog";
import EmailPanel from "./EmailPanel";
import FilterPanel from "./FilterPanel";
import FixBanner from "./FixBanner";
import FlowCanvas from "./FlowCanvas";
import JourneyPanel from "./JourneyPanel";
import PublishGate from "./PublishGate";
import { DefaultPanel, ExitRulesPanel } from "./SidePanels";
import { openModal, sendTestEmail } from "./save-changes";

const listUrl = (product) =>
  `/coaching-portal/custom-workflows/?access=${product}&view=email`;

const PublishToggle = ({ automation }) => {
  const { t } = useTranslation("coaching-portal");
  const on = automation.status === "active";
  const onChange = () => {
    if (on) {
      openModal("turnOffAutomation", { automation: automation._id });
      return;
    }
    openModal("turnOnAutomation", { automation: automation._id });
  };
  return (
    <label className="ab-publish">
      <span>{t("emailAutomations.builder.header.publish")}</span>
      <input
        type="checkbox"
        role="switch"
        checked={on}
        aria-checked={on}
        onChange={onChange}
      />
    </label>
  );
};

const BuilderHeader = ({ automation, testPosition, activityTab }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.${key}`);
  return (
    <header className="ab-header">
      <div className="ab-header__title">
        <a className="ab-back" href={listUrl(automation.product)}>
          <img src="/img/new-icons/arrow-left.svg" alt={tr("header.back")} />
        </a>
        <div>
          <nav aria-label="breadcrumb" className="ab-breadcrumb">
            <span>{tr("breadcrumb.custom")}</span>
            <span>{tr(`breadcrumb.products.${automation.product}`)}</span>
            <a href={listUrl(automation.product)}>{tr("breadcrumb.list")}</a>
            <span>{automation.name}</span>
          </nav>
          <h2>{automation.name}</h2>
        </div>
      </div>
      <div className="ab-header__tools">
        <div className="ab-tabs">
          <a
            href="/actions/refresh?tab=&person=&status=&search=&sort=&dir=&page="
            data-current={activityTab ? undefined : ""}
          >
            {tr("header.flow")}
          </a>
          <a
            href="/actions/refresh?tab=activity&step=&person="
            data-current={activityTab ? "" : undefined}
          >
            {tr("header.activityLog")}
          </a>
        </div>
        <button
          type="button"
          data-button-type="secondary"
          aria-disabled={testPosition === undefined}
          title={testPosition === undefined ? tr("header.noEmail") : undefined}
          onClick={() => {
            if (testPosition !== undefined) {
              sendTestEmail(automation._id, testPosition);
            }
          }}
        >
          {tr("header.sendTest")}
        </button>
        <PublishToggle automation={automation} />
      </div>
    </header>
  );
};

const SidePanel = ({
  detail,
  selected,
  triggers,
  definition,
  filterOptions,
  sendingSettings,
}) => {
  const { automation, steps, issues } = detail;
  if (selected === "exit") {
    return (
      <ExitRulesPanel
        automation={automation}
        exitEvents={triggers.exitEvents}
        closeHref="/actions/refresh?step="
      />
    );
  }
  const index = Number(selected);
  if (steps[index]?.type === "email") {
    return (
      <EmailPanel
        key={`${automation._id}-${index}`}
        automationId={automation._id}
        steps={steps}
        index={index}
        mergeFields={definition.mergeFields ?? []}
        sendingSettings={sendingSettings}
        issues={issues}
        closeHref="/actions/refresh?step="
      />
    );
  }
  if (steps[index]?.type === "filter") {
    return (
      <FilterPanel
        key={`${automation._id}-${index}`}
        automationId={automation._id}
        steps={steps}
        index={index}
        definition={definition}
        options={filterOptions}
        closeHref="/actions/refresh?step="
      />
    );
  }
  return <DefaultPanel />;
};

const AutomationBuilder = () => {
  const { locals } = useRillContext();
  const {
    automationDetail,
    automationTriggers,
    automationFilterOptions,
    sendingSettings,
    automationActivity,
    automationJourney,
    query,
  } = locals;
  const activityTab = query.tab === "activity" && Boolean(automationActivity);

  const { automation } = automationDetail;
  const definition = automationTriggers.triggers.find(
    (trigger) => trigger.key === automation.trigger.key
  );
  const selected =
    query.step === undefined || query.step === ""
      ? undefined
      : String(query.step);

  const { steps = [], issues = [], suggestions = [] } = automationDetail;
  // What the test sends: the email being edited, or else the first one
  const emailIndex = steps.findIndex((step) => step.type === "email");
  const testPosition =
    steps[Number(selected)]?.type === "email" ? Number(selected) : emailIndex;
  const fix = String(query.fix) === "1" && automation.status !== "active";

  return (
    <main className="ab">
      <PublishGate
        automation={automation}
        peopleInside={automationDetail.peopleInside ?? 0}
      />
      <BuilderHeader
        automation={automation}
        testPosition={testPosition === -1 ? undefined : testPosition}
        activityTab={activityTab}
      />
      {activityTab ? (
        <div className="ab-body ab-body--log">
          <ActivityLog
            automationId={automation._id}
            product={automation.product}
            activity={automationActivity}
            options={automationFilterOptions ?? undefined}
            query={query}
          />
          {query.person && automationJourney ? (
            <JourneyPanel
              automationId={automation._id}
              enrollmentId={query.person}
              journey={automationJourney}
              triggers={automationTriggers.triggers}
              options={automationFilterOptions ?? undefined}
            />
          ) : null}
        </div>
      ) : (
        <div className="ab-body">
          <section className="ab-canvas">
            {fix ? (
              <FixBanner
                automationId={automation._id}
                issues={issues}
                suggestions={suggestions}
              />
            ) : null}
            <FlowCanvas
              detail={automationDetail}
              definition={definition}
              options={automationFilterOptions ?? undefined}
              selected={selected}
              issues={fix ? issues : []}
            />
          </section>
          <aside className="ab-side">
            <SidePanel
              detail={automationDetail}
              selected={selected}
              triggers={automationTriggers}
              definition={definition}
              filterOptions={automationFilterOptions ?? undefined}
              sendingSettings={sendingSettings ?? undefined}
            />
          </aside>
        </div>
      )}
    </main>
  );
};

export default AutomationBuilder;
